import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { Recycle, Layers, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const REUSE_BREAKDOWN = [
    { name: "Drawings",             value: 412, color: "#D4AF37" },
    { name: "Calculations",         value: 321, color: "#3B82F6" },
    { name: "Specifications",       value: 76,  color: "#10B981" },
    { name: "Templates",            value: 612, color: "#F59E0B" },
    { name: "Vendor Packages",      value: 145, color: "#94A3B8" },
    { name: "Lessons Learned",      value: 218, color: "#EF4444" },
    { name: "Data Sheets",          value: 642, color: "#8B5CF6" },
    { name: "Engineering Standards",value: 84,  color: "#22D3EE" },
];

const TREND = [
    { quarter: "Q1'24", reuse: 12.1 },
    { quarter: "Q2'24", reuse: 17.4 },
    { quarter: "Q3'24", reuse: 22.8 },
    { quarter: "Q4'24", reuse: 27.2 },
    { quarter: "Q1'25", reuse: 31.5 },
    { quarter: "Q2'25", reuse: 36.9 },
    { quarter: "Q3'25", reuse: 41.4 },
    { quarter: "Q4'25", reuse: 44.9 },
    { quarter: "Q1'26", reuse: 47.3 },
];

export default function KnowledgeReuse() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/edi/summary").then((r) => setData(r.data)).catch(() => toast.error("Failed to load knowledge reuse"));
    }, []);
    if (!data) return <div className="kicker text-slate-500">Loading knowledge reuse…</div>;

    const total = REUSE_BREAKDOWN.reduce((s, r) => s + r.value, 0);

    return (
        <div className="space-y-8" data-testid={EDI.knowledgeReuseRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Knowledge Reuse Dashboard
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        Corporate reuse velocity — what is being reused, how much, and where the biggest opportunities remain.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">YTD · Feb 2026</span>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    ["Knowledge Reuse", "47.3%", Recycle,  "Portfolio average"],
                    ["Reusable Assets", "84,120", Layers,   "Curated"],
                    ["Total Reuses (YTD)", total.toLocaleString(), Layers, "Assets applied"],
                    ["Growth (12m)", "+35.2 pp", Recycle, "vs. Q1'25"],
                ].map(([k, v, Icon, s]) => (
                    <div key={k} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                        <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                        <Icon className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <div className="mt-2 kicker text-slate-400 text-[10px]">{k}</div>
                        <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                        <div className="mt-1 text-[11px] text-slate-500 font-body">{s}</div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <div className="glass rounded-xl p-6">
                    <div className="kicker text-slate-400">Reuse composition</div>
                    <h3 className="mt-1 font-display font-black text-white text-xl tracking-tight">By asset class</h3>
                    <div className="h-[300px] mt-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={REUSE_BREAKDOWN} dataKey="value" nameKey="name" innerRadius={60} outerRadius={110} paddingAngle={2} strokeWidth={0}>
                                    {REUSE_BREAKDOWN.map((e) => <Cell key={e.name} fill={e.color} />)}
                                </Pie>
                                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }} />
                                <Legend wrapperStyle={{ color: "#94A3B8", fontFamily: "Manrope", fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="glass rounded-xl p-6">
                    <div className="kicker text-slate-400">Reuse velocity</div>
                    <h3 className="mt-1 font-display font-black text-white text-xl tracking-tight">Portfolio reuse % over time</h3>
                    <div className="h-[300px] mt-3 -ml-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={TREND}>
                                <CartesianGrid strokeDasharray="3 6" stroke="rgba(148,163,184,0.12)" vertical={false} />
                                <XAxis dataKey="quarter" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <YAxis stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }} />
                                <Line dataKey="reuse" stroke="#D4AF37" strokeWidth={2.4} dot={{ r: 3, fill: "#D4AF37" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="glass rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/60">
                    <h3 className="font-display font-black text-white tracking-tight">Reuse detail by asset class</h3>
                </div>
                <table className="w-full text-sm font-body">
                    <thead>
                        <tr className="text-slate-500 kicker text-[10px]">
                            <th className="text-left px-4 py-3">Asset Class</th>
                            <th className="text-right px-4 py-3">Reuses (YTD)</th>
                            <th className="text-right px-4 py-3">Share</th>
                        </tr>
                    </thead>
                    <tbody>
                        {REUSE_BREAKDOWN.map((r) => (
                            <tr key={r.name} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                <td className="px-4 py-2.5 text-slate-100 flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full" style={{ background: r.color }} />
                                    {r.name}
                                </td>
                                <td className="px-4 py-2.5 text-right text-slate-200 font-medium">{r.value.toLocaleString()}</td>
                                <td className="px-4 py-2.5 text-right text-[var(--sgsb-gold)] font-display font-bold">{(r.value / total * 100).toFixed(1)}%</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
